import React from "react";
import {
  HeroVideo,
  ImageTile,
  PrintableCard,
  Spotlight,
  Columns,
  Newsletter,
} from "@gonoodle/gn-universe-ui";
import {
  generatePath,
  concatenateQueryParams,
} from "@gonoodle/gn-universe-utils";
import { useRouter } from "next/router";
import { QueryClient, useQueries } from "react-query";
import { dehydrate } from "react-query/hydration";
import { find, sampleSize } from "lodash";

import Layout from "../components/Layout";
import VideoTile from "../components/VideoTile";
import api from "../api";
import { createImgSources } from "../utils/imgSources";
import { selectSpotlight } from "../selectors";
import {
  QUERY_KEYS,
  ROUTE_PREFIX,
  PAGES_TYPES,
  SECTIONS,
  SECTIONS_TYPES,
  ROUTE_PATHS,
  SPOTLIGHT_TYPES,
  USER_TYPES,
  ROUTE_PERMISSIONS,
} from "../constants";
import { WithContentLink, Link, withAuthorization } from "../containers";
import { useUser } from "../contexts/user";
import { useLogEvent } from "../contexts/Analytics";
import { pushExternal } from "../utils/route";
import contentTypes from "../constants/contentTypes";

const JSURL = require("jsurl");

const PrintableCardWithLink = WithContentLink(PrintableCard);
const HeroVideoWithLink = WithContentLink(HeroVideo);

const Home = () => {
  const router = useRouter();
  const client = api();
  const { user } = useUser();
  const userType = find(USER_TYPES, { id: user.userTypeId })?.name;
  const [redirectToSpotlight, setRedirectToSpotlight] = React.useState(false);
  const [
    newsletterSignUpCompleted,
    setNewsletterSignUpCompleted,
  ] = React.useState(false);

  const [
    heroVideoRes,
    spotlightRes,
    topicsShelfRes,
    newOnShelfRes,
    champiverseShelfRes,
    featuredActivitiesShelfRes,
  ] = useQueries([
    {
      queryKey: QUERY_KEYS.HERO_VIDEO,
      queryFn: () => client.getHeroVideo(),
      staleTime: Infinity,
    },
    {
      queryKey: QUERY_KEYS.HOME_SPOTLIGHT,
      queryFn: () => client.getHomeSpotlight(),
      staleTime: Infinity,
      select: (data) => selectSpotlight(data),
    },
    {
      queryKey: [QUERY_KEYS.SHELVES, 1],
      queryFn: () => client.getShelf(1),
      staleTime: Infinity,
    },
    {
      queryKey: [QUERY_KEYS.SHELVES, 2],
      queryFn: () => client.getShelf(2),
      staleTime: Infinity,
    },
    {
      queryKey: [QUERY_KEYS.SHELVES, 3],
      queryFn: () => client.getShelf(3),
      staleTime: Infinity,
    },
    {
      queryKey: [QUERY_KEYS.SHELVES, 4],
      queryFn: () => client.getShelf(4),
      staleTime: Infinity,
    },
  ]);

  const { data: spotlight } = spotlightRes;
  const { data: heroVideo } = heroVideoRes;
  const { data: topicsShelf } = topicsShelfRes;
  const { data: champiverseShelf } = champiverseShelfRes;
  const { data: newOnShelf } = newOnShelfRes;
  const { data: featuredActivitiesShelf } = featuredActivitiesShelfRes;

  const heroImgSource = createImgSources(heroVideo?.images?.heroImage);
  const heroLogoSource = createImgSources(heroVideo?.images?.logo);
  const spotlightColorTheme = spotlight?.type ? "dark" : spotlight?.textTheme;
  const spotlightUrl = React.useMemo(() => {
    if (spotlight) {
      if (spotlight.isExternal) {
        return spotlight.externalUrl;
      }
      if (spotlight.urlPath) {
        return spotlight.urlPath;
      }
    }
    return undefined;
  }, [spotlight]);

  const topics = React.useMemo(
    () =>
      topicsShelf?.type === contentTypes.PROGRAMMED
        ? topicsShelf.content
        : sampleSize(topicsShelf?.content, 5),
    [topicsShelf],
  );
  const champiverseVideos = React.useMemo(
    () =>
      champiverseShelf?.type === contentTypes.PROGRAMMED
        ? champiverseShelf.content
        : sampleSize(champiverseShelf?.content, 4),
    [champiverseShelf],
  );

  React.useEffect(() => {
    if (spotlight && spotlightUrl && redirectToSpotlight === true) {
      if (spotlight.isExternal) {
        pushExternal(spotlightUrl, {
          sourcePage: PAGES_TYPES.HOME,
          sourcePageType: PAGES_TYPES.HOME,
          sourceElement: SECTIONS_TYPES.SPOTLIGHT,
          sourceName: spotlight.title,
        });
      } else if (spotlight.urlPath) {
        const { destinationType } = spotlight;
        if (
          destinationType === SPOTLIGHT_TYPES.VIDEO ||
          destinationType === SPOTLIGHT_TYPES.ACTIVITY
        ) {
          const pathWithParams = concatenateQueryParams(
            `/${
              destinationType === SPOTLIGHT_TYPES.VIDEO
                ? ROUTE_PREFIX.VIDEOS
                : ROUTE_PREFIX.ACTIVITIES
            }/[...identity]`,
            {
              referrer: JSURL.stringify({
                sourcePage: PAGES_TYPES.HOME,
                sourcePageType: PAGES_TYPES.HOME,
                sourceElement: SECTIONS_TYPES.SPOTLIGHT,
                sourceName: spotlight.title,
              }),
            },
          );

          router.push(pathWithParams, spotlightUrl);
        } else {
          router.push(spotlightUrl);
        }
      }
    }
  }, [redirectToSpotlight, router, spotlight, spotlightUrl]);

  useLogEvent({
    event: "Link Clicked",
    properties: {
      title: spotlight?.title,
      url: spotlightUrl,
    },
    options: {
      referrer: {
        sourcePage: PAGES_TYPES.HOME,
        sourcePageType: PAGES_TYPES.HOME,
        sourceElement: SECTIONS_TYPES.SPOTLIGHT,
        sourceName: spotlight?.title,
      },
      enabled: redirectToSpotlight,
    },
  });

  useLogEvent({
    event: "Newsletter Sign Up",
    properties: {},
    options: {
      enabled: newsletterSignUpCompleted,
      referrer: {
        sourcePage: PAGES_TYPES.HOME,
        sourcePageType: PAGES_TYPES.HOME,
      },
    },
  });

  return (
    <Layout pageTitle="Home" pageDescription="">
      <div className="container pt-sm pb-lg">
        {heroVideo && (
          <HeroVideoWithLink
            title={heroVideo.title}
            description={heroVideo.description}
            source={heroVideo.loopingVideo}
            fallbackImageSource={heroImgSource}
            heroLogo={heroLogoSource}
            id={heroVideo.id}
            slug={heroVideo.slug}
            buttonText={heroVideo.buttonText}
            mode={heroVideo.textTheme}
            contentLinkAsBase={`/${ROUTE_PREFIX.VIDEOS}`}
            contentLinkHrefBase={`/${ROUTE_PREFIX.VIDEOS}/[...identity]`}
            linkParams={{
              sourcePage: PAGES_TYPES.HOME,
              sourcePageType: PAGES_TYPES.HOME,
              sourceElement: SECTIONS_TYPES.HERO,
              sourceName:
                heroVideo.type === "rotation"
                  ? SECTIONS.ROTATION
                  : heroVideo.title,
            }}
          />
        )}
      </div>
      <div className="">
        {/* TOPICS  */}
        {topicsShelf && (
          <Columns title={topicsShelf.title} cols={5} testID="homepageShelfOne">
            {topics.map((topic) => (
              <div
                className="flex flex-col justify-center relative"
                key={topic.id}
              >
                <Link
                  key={topic.id}
                  href={generatePath(ROUTE_PATHS.TAGS, {
                    id: topic.id,
                    slug: topic.slug,
                  })}
                  events={[
                    {
                      event: "Link Clicked",
                      properties: {
                        title: topic.name,
                        sourcePage: PAGES_TYPES.HOME,
                        sourcePageType: PAGES_TYPES.HOME,
                        sourceElement: SECTIONS_TYPES.SHELF,
                        sourceName: topicsShelf.title,
                      },
                    },
                  ]}
                >
                  <ImageTile
                    alt={topic.slug}
                    name={topic.name}
                    sources={topic.images.tileImage}
                    format={topicsShelf.tileSize}
                  />
                </Link>
                <span className="sr-only absolute left-1/4 uppercase text-white bottom-0">
                  {topic.name}
                </span>
              </div>
            ))}
          </Columns>
        )}

        {/* NEW ON GONOODLE  */}
        {newOnShelf ? (
          <Columns title={newOnShelf.title} cols={4} testID="homepageShelfTwo">
            {newOnShelf.content.map((video) => (
              <li key={video.id}>
                <VideoTile
                  id={video.id}
                  slug={video.slug}
                  title={video.title}
                  description={video.description}
                  images={video.images}
                  duration={video.duration}
                  sponsor={{
                    expiration: video.attribution?.expiration,
                    type: video.attribution?.type,
                  }}
                  impressionsProps={{
                    sourcePage: PAGES_TYPES.HOME,
                    sourcePageType: PAGES_TYPES.HOME,
                    sourceName: newOnShelf.title,
                    sourceElement: SECTIONS_TYPES.SHELF,
                  }}
                  linkParams={{
                    sourcePage: PAGES_TYPES.HOME,
                    sourcePageType: PAGES_TYPES.HOME,
                    sourceName: newOnShelf.title,
                    sourceElement: SECTIONS_TYPES.SHELF,
                  }}
                />
              </li>
            ))}
          </Columns>
        ) : null}

        {/* SPOTLIGHT  */}
        {spotlight && (
          <div className="container py-lg" data-testid="homepageSpotlight">
            <Spotlight
              source={spotlight.loopingVideo}
              fallbackImageSource={spotlight.image}
              title={spotlight.title}
              description={spotlight.description}
              onClick={() => setRedirectToSpotlight(true)}
              buttonLabel={spotlight.buttonText}
              bgColor={spotlight.hexColor}
              textTheme={spotlightColorTheme}
            />
          </div>
        )}

        {/* MEET THE CHAMPS  */}
        {champiverseShelf && (
          <Columns
            title={champiverseShelf.title}
            cols={4}
            testID="homepageShelfThree"
          >
            {champiverseVideos.map((video) => (
              <li key={video.id}>
                <VideoTile
                  id={video.id}
                  slug={video.slug}
                  title={video.title}
                  description={video.description}
                  images={video.images}
                  duration={video.duration}
                  sponsor={{
                    expiration: video.attribution?.expiration,
                    type: video.attribution?.type,
                  }}
                  impressionsProps={{
                    sourcePage: PAGES_TYPES.HOME,
                    sourcePageType: PAGES_TYPES.HOME,
                    sourceName: champiverseShelf.title,
                    sourceElement: SECTIONS_TYPES.SHELF,
                  }}
                  linkParams={{
                    sourcePage: PAGES_TYPES.HOME,
                    sourcePageType: PAGES_TYPES.HOME,
                    sourceName: champiverseShelf.title,
                    sourceElement: SECTIONS_TYPES.SHELF,
                  }}
                />
              </li>
            ))}
          </Columns>
        )}

        {/* FEATURED ACTIVITIES  */}
        {featuredActivitiesShelf && (
          <Columns
            title={featuredActivitiesShelf.title}
            cols={5}
            testID="homepageShelfFour"
          >
            {featuredActivitiesShelf.content.map(
              ({ id, slug, title, image }) => (
                <PrintableCardWithLink
                  key={`printable-${id}-${title}`}
                  id={id}
                  slug={slug}
                  mode="dark"
                  sources={image}
                  title={title}
                  contentLinkAsBase={`/${ROUTE_PREFIX.ACTIVITIES}`}
                  contentLinkHrefBase={`/${ROUTE_PREFIX.ACTIVITIES}/[...identity]`}
                  linkParams={{
                    sourceElement: SECTIONS_TYPES.SHELF,
                    sourceName: featuredActivitiesShelf.title,
                    sourcePage: PAGES_TYPES.HOME,
                    sourcePageType: PAGES_TYPES.HOME,
                  }}
                />
              ),
            )}
          </Columns>
        )}
      </div>
      {!user.isAnonymous && (
        <Newsletter
          handleSubmit={() => setNewsletterSignUpCompleted(true)}
          userType={userType}
        />
      )}
    </Layout>
  );
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  const client = api(ctx);
  const queryClient = new QueryClient();

  await Promise.all([
    queryClient.prefetchQuery(QUERY_KEYS.CONTENT_TAG_LIST, async () =>
      client.getContentTags(),
    ),
    queryClient.prefetchQuery(QUERY_KEYS.CHAMPIVERSE_VIDEOS, async () =>
      client.getChampiverseVideos(),
    ),
    queryClient.prefetchQuery(QUERY_KEYS.RECENTLY_PUBLISHED_VIDEOS, async () =>
      client.getRecentlyPublishedVideos(),
    ),
    queryClient.prefetchQuery(QUERY_KEYS.HERO_VIDEO, async () =>
      client.getHeroVideo(),
    ),
    queryClient.prefetchQuery(QUERY_KEYS.NEWEST_PRINTABLES, async () =>
      client.getNewestPrintables(),
    ),
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      trackPage: true,
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

export default Home;
